////////////////////////////////////////////////////////////////////////////////
// Copyright 2019 Federated Wireless.
//
// GOVERNMENT PURPOSE RIGHTS
//
// Contractor Name:    Federated Wireless
// Contractor Address: 3865 Wilson Blvd, Arlington, VA 22203
//
// The Government's rights to use, modify, reproduce, release, perform, display,
// or disclose this software are restricted by paragraph (b)(2) of the Rights in
// Noncommercial Computer Software and Noncommercial Computer Software
// Documentation clause contained in the 7013 Clause. Any reproduction of the
// software or portions thereof marked with this legend must also reproduce the
// markings.
// This source code delivery is exclusively for Government Purposes pertaining
// to this program; no commercial use. The Computer Software Configuration
// Items were delivered for only the purposes spelled out by the contract
// delivered under.
//
// Export Warning - The technical data in this document (or file) is
// controlled for export under the International Traffic in Arms Regulations
// (ITAR), 22CFR 120-130.  Violations of these export laws may be subject to
// fines and penalties under the Arms Export Control Act (22 U.S.C. 2778).
//
////////////////////////////////////////////////////////////////////////////////
//
// Security Classification: UNCLASSIFIED
//
////////////////////////////////////////////////////////////////////////////////

import React, { Component } from 'react';
import { Field, reduxForm, formValueSelector, change } from 'redux-form';
import renderField from './renderField'
import validate from './validate'
import { connect } from 'react-redux'
import ReactTooltip from 'react-tooltip';
import Switch from "react-switch";



class TddFormPage extends Component {
  constructor(props) {
    super(props);
    this.getAntennaPath = this.getAntennaPath.bind(this);
    this.state = { enterPeakPower: false };
    this.handleChange = this.handleChange.bind(this);
  }
  state = {
    tilt:0,
    azimuth: 0
  };

  componentDidMount() {
    const initialVals = this.props.getInitialValues();
    this.props.initialize(initialVals);
    this.setState({tilt: initialVals.tilt, azimuth: initialVals.azimuth});
  }

  renderError = ({ meta: { touched, error } }) =>
    touched && 
    error ? <span>{error}</span> : false

  angleToBearing = (angle) => {
    // input: angle in degrees in range (-Inf, Inf)
    // result: bearing in degrees in range [0, 360)

    // convert to range [-360, 360]
    const modAngle = ((angle % 360.0) + 360.0) % 360.0;

    // convert to range [0, 360)
    const bearing = modAngle + (modAngle < 0.0 ? 360.0 : 0.0);

    return bearing;
  }

  getRadiusInPixelsForGain(gain) {
    const outer_radius = 45; // outer radius of the rendered pattern in pixels (relative to SVG's viewbox)

    const display_factor = 70; // arbitrarily chosen for aesthetic reasons

    const max_gain = Math.pow(10, 20 / display_factor);
    const min_gain = Math.pow(10, -50 / display_factor);
    const gain_range = max_gain - min_gain;

    const capped_gain = Math.min(max_gain, Math.max(min_gain, Math.pow(10, gain / display_factor)));
    const scaled_gain = (capped_gain - min_gain) / gain_range;

    return scaled_gain * outer_radius;
  }

  getAntennaPath(directional_gain_dbi, rotation) {
    const half_width = 50; // half-width of the rendered pattern in pixels (relative to SVG's viewbox)

    let path = '';
    directional_gain_dbi.forEach((gain, index) => {
      const theta = (index + rotation) / 180 * Math.PI;
      const r = this.getRadiusInPixelsForGain(gain);
      const x = half_width + r * Math.sin(theta);
      const y = half_width - r * Math.cos(theta);

      path += `${(index === 0 ? 'M' : 'L') + x} ${y} `;
    });
    path += 'Z';

    return path;
  }    

  renderAntennaDetails(antenna) {
    let ret = []
    let key = 0

    ret.push(<React.Fragment key={key++}><b>Frequency:</b> {this.props.antenna_specs[antenna]["lower_frequency_mhz"]}-{this.props.antenna_specs[antenna]["upper_frequency_mhz"]}MHz<br/></React.Fragment>)
    ret.push(<React.Fragment key={key++}><b>Polarisation:</b> {this.props.antenna_specs[antenna]["polarisation"]}<br/></React.Fragment>)
    ret.push(<React.Fragment key={key++}><b>Gain:</b> {this.props.antenna_specs[antenna]["nominal_gain_dbi"]} dBi<br/></React.Fragment>)
    ret.push(<React.Fragment key={key++}><b>Horizontal 3dB Beamwidth:</b> {this.props.antenna_specs[antenna]["horizontal_beamwidth_deg"]}°<br/></React.Fragment>)
    ret.push(<React.Fragment key={key++}><b>Vertical 3dB Beamwidth:</b> {this.props.antenna_specs[antenna]["vertical_beamwidth_deg"]}°<br/></React.Fragment>)
    ret.push(<React.Fragment key={key++}><b>Tilt:</b> {this.props.antenna_specs[antenna]["lower_tilt_deg"]}-{this.props.antenna_specs[antenna]["upper_tilt_deg"]}°<br/></React.Fragment>)
    ret.push(
      <React.Fragment key={key++}>
        <div className="row">
          <div className="col-sm-6 text-center">
            <svg id="horizontal-pattern-plot-svg" width="200" height="200" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <line x1="0" y1="50" x2="100" y2="50" style={{stroke:'lightgray', strokeWidth:'0.25'}}/>
              <line x1="50" y1="0" x2="50" y2="100" style={{stroke:'lightgray', strokeWidth:'0.25'}}/>
              <line x1="18.2" y1="18.2" x2="81.8" y2="81.8" style={{stroke:'lightgray', strokeWidth:'0.25'}}/>
              <line x1="18.2" y1="81.8" x2="81.8" y2="18.2" style={{stroke:'lightgray', strokeWidth:'0.25'}}/>
              <circle cx="50" cy="50" r={this.getRadiusInPixelsForGain(20)} stroke="gray" strokeWidth="0.25" fill="none" />
              <circle cx="50" cy="50" r={this.getRadiusInPixelsForGain(10)} stroke="lightgray" strokeWidth="0.25" fill="none" />
              <circle cx="50" cy="50" r={this.getRadiusInPixelsForGain(0)} stroke="lightgray" strokeWidth="0.5" fill="none" />
              <circle cx="50" cy="50" r={this.getRadiusInPixelsForGain(-10)} stroke="lightgray" strokeWidth="0.25" fill="none" />
              <circle cx="50" cy="50" r={this.getRadiusInPixelsForGain(-20)} stroke="lightgray" strokeWidth="0.25" fill="none" />
              <text x="51" y="4" fill="gray" fontSize="4pt">N</text>
              <text x="51" y="100" fill="gray" fontSize="4pt">S</text>
              <text x="96" y="49" fill="gray" fontSize="4pt">E</text>
              <text x="0" y="49" fill="gray" fontSize="4pt">W</text>
              <text x="83" y="19" fill="gray" fontSize="3pt">+20dBi</text>
              <text x="75" y="29" fill="gray" fontSize="3pt">+10</text>
              <text x="67" y="37" fill="gray" fontSize="3pt">0</text>
              <text x="60" y="42" fill="gray" fontSize="3pt">-10</text>
              <text x="55" y="46" fill="gray" fontSize="3pt">-20</text>
              <line x1="50" y1="50" x2={50 + 47 * Math.sin(this.state.azimuth / 180 * Math.PI)} y2={50 - 47 * Math.cos(this.state.azimuth  / 180 * Math.PI)} style={{stroke:'blue', strokeWidth:'0.2'}}/>
              <path className="antenna-path" d={this.getAntennaPath(this.props.antenna_specs[antenna]["patterns"][this.state.tilt]["horizontal_gains_dbi"], this.state.azimuth)} style={{fill:'none', stroke:'blue'}}/>
            </svg>
            <span className="text-center">Horizontal</span>
          </div>
          <div className="col-sm-6 text-center">
            <svg id="vertical-pattern-plot-svg" width="200" height="200" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <line x1="0" y1="50" x2="100" y2="50" style={{stroke:'lightgray', strokeWidth:'0.25'}}/>
              <line x1="50" y1="0" x2="50" y2="100" style={{stroke:'lightgray', strokeWidth:'0.25'}}/>
              <line x1="18.2" y1="18.2" x2="81.8" y2="81.8" style={{stroke:'lightgray', strokeWidth:'0.25'}}/>
              <line x1="18.2" y1="81.8" x2="81.8" y2="18.2" style={{stroke:'lightgray', strokeWidth:'0.25'}}/>
              <circle cx="50" cy="50" r={this.getRadiusInPixelsForGain(20)} stroke="gray" strokeWidth="0.25" fill="none" />
              <circle cx="50" cy="50" r={this.getRadiusInPixelsForGain(10)} stroke="lightgray" strokeWidth="0.25" fill="none" />
              <circle cx="50" cy="50" r={this.getRadiusInPixelsForGain(0)} stroke="lightgray" strokeWidth="0.5" fill="none" />
              <circle cx="50" cy="50" r={this.getRadiusInPixelsForGain(-10)} stroke="lightgray" strokeWidth="0.25" fill="none" />
              <circle cx="50" cy="50" r={this.getRadiusInPixelsForGain(-20)} stroke="lightgray" strokeWidth="0.25" fill="none" />
              <text x="96" y="49" fill="gray" fontSize="3pt">0&deg;</text>
              <text x="83" y="19" fill="gray" fontSize="3pt">+20dBi</text>
              <text x="75" y="29" fill="gray" fontSize="3pt">+10</text>
              <text x="67" y="37" fill="gray" fontSize="3pt">0</text>
              <text x="60" y="42" fill="gray" fontSize="3pt">-10</text>
              <text x="55" y="46" fill="gray" fontSize="3pt">-20</text>
              <line x1="50" y1="50" x2={50 + 47 * Math.sin((90 + this.state.tilt) / 180 * Math.PI)} y2={50 - 47 * Math.cos((90 + this.state.tilt)  / 180 * Math.PI)} style={{stroke:'red', strokeWidth:'0.2'}}/>
              <path className="antenna-path" d={this.getAntennaPath(this.props.antenna_specs[antenna]["patterns"][this.state.tilt]["vertical_gains_dbi"], 90)} style={{fill:'none', stroke:'red'}}/>
            </svg>
            <span className="text-center">Vertical</span>
          </div>
        </div>
        <div id="hiddenplots" style={{visibility: 'hidden', display:'none'}} >
        <svg id="horizontal-pattern-plot-svg-pdf" width="200" height="200" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <line x1="0" y1="50" x2="100" y2="50" style={{stroke:'lightgray', strokeWidth:'0.25'}}/>
              <line x1="50" y1="0" x2="50" y2="100" style={{stroke:'lightgray', strokeWidth:'0.25'}}/>
              <line x1="18.2" y1="18.2" x2="81.8" y2="81.8" style={{stroke:'lightgray', strokeWidth:'0.25'}}/>
              <line x1="18.2" y1="81.8" x2="81.8" y2="18.2" style={{stroke:'lightgray', strokeWidth:'0.25'}}/>
              <circle cx="50" cy="50" r={this.getRadiusInPixelsForGain(20)} stroke="gray" strokeWidth="0.25" fill="none" />
              <circle cx="50" cy="50" r={this.getRadiusInPixelsForGain(10)} stroke="lightgray" strokeWidth="0.25" fill="none" />
              <circle cx="50" cy="50" r={this.getRadiusInPixelsForGain(0)} stroke="lightgray" strokeWidth="0.5" fill="none" />
              <circle cx="50" cy="50" r={this.getRadiusInPixelsForGain(-10)} stroke="lightgray" strokeWidth="0.25" fill="none" />
              <circle cx="50" cy="50" r={this.getRadiusInPixelsForGain(-20)} stroke="lightgray" strokeWidth="0.25" fill="none" />
              <text x="83" y="19" fill="gray" fontSize="3pt">+20dBi</text>
              <text x="75" y="29" fill="gray" fontSize="3pt">+10</text>
              <text x="67" y="37" fill="gray" fontSize="3pt">0</text>
              <text x="60" y="42" fill="gray" fontSize="3pt">-10</text>
              <text x="55" y="46" fill="gray" fontSize="3pt">-20</text>
              <line x1="50" y1="50" x2={50 + 47 * Math.sin(0 / 180 * Math.PI)} y2={50 - 47 * Math.cos(0  / 180 * Math.PI)} style={{stroke:'blue', strokeWidth:'0.2'}}/>
              <path className="antenna-path" d={this.getAntennaPath(this.props.antenna_specs[antenna]["patterns"][0]["horizontal_gains_dbi"], 0)} style={{fill:'none', stroke:'blue'}}/>
            </svg>
            <svg id="vertical-pattern-plot-svg-pdf" width="200" height="200" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <line x1="0" y1="50" x2="100" y2="50" style={{stroke:'lightgray', strokeWidth:'0.25'}}/>
              <line x1="50" y1="0" x2="50" y2="100" style={{stroke:'lightgray', strokeWidth:'0.25'}}/>
              <line x1="18.2" y1="18.2" x2="81.8" y2="81.8" style={{stroke:'lightgray', strokeWidth:'0.25'}}/>
              <line x1="18.2" y1="81.8" x2="81.8" y2="18.2" style={{stroke:'lightgray', strokeWidth:'0.25'}}/>
              <circle cx="50" cy="50" r={this.getRadiusInPixelsForGain(20)} stroke="gray" strokeWidth="0.25" fill="none" />
              <circle cx="50" cy="50" r={this.getRadiusInPixelsForGain(10)} stroke="lightgray" strokeWidth="0.25" fill="none" />
              <circle cx="50" cy="50" r={this.getRadiusInPixelsForGain(0)} stroke="lightgray" strokeWidth="0.5" fill="none" />
              <circle cx="50" cy="50" r={this.getRadiusInPixelsForGain(-10)} stroke="lightgray" strokeWidth="0.25" fill="none" />
              <circle cx="50" cy="50" r={this.getRadiusInPixelsForGain(-20)} stroke="lightgray" strokeWidth="0.25" fill="none" />
              <text x="96" y="49" fill="gray" fontSize="3pt">0&deg;</text>
              <text x="83" y="19" fill="gray" fontSize="3pt">+20dBi</text>
              <text x="75" y="29" fill="gray" fontSize="3pt">+10</text>
              <text x="67" y="37" fill="gray" fontSize="3pt">0</text>
              <text x="60" y="42" fill="gray" fontSize="3pt">-10</text>
              <text x="55" y="46" fill="gray" fontSize="3pt">-20</text>
              <line x1="50" y1="50" x2={50 + 47 * Math.sin((90 + 0) / 180 * Math.PI)} y2={50 - 47 * Math.cos((90 + 0)  / 180 * Math.PI)} style={{stroke:'red', strokeWidth:'0.2'}}/>
              <path className="antenna-path" d={this.getAntennaPath(this.props.antenna_specs[antenna]["patterns"][0]["vertical_gains_dbi"], 90)} style={{fill:'none', stroke:'red'}}/>
            </svg>
            </div>
      </React.Fragment>
    )
    return(ret)
  }

  standardizeDecimalPlaces(num){
    return parseFloat(num.toFixed(4))
  }

  handleChange(enterPeakPower) {
    this.setState({ enterPeakPower });
  }

  render(){
    const { handleSubmit, pristine, antenna } = this.props;

    return (
    <React.Fragment>
      <form onSubmit={handleSubmit}>
        <h4>Choose antenna hardware</h4>
        <div>
          <Field
            name="antenna"
            component={({ input, meta: { touched, error } }) => (
              <div>
                <select {...input}>
                  <option value="">Select antenna...</option>
                  {(()=>{
                    let options = []
                    let key =0
                    for (let antenna in this.props.antenna_specs){
                      options.push(<option key={key++} value={antenna}>{antenna}</option>)
                    }
                    return options
                  })()}
                </select>
                {touched && error && <span>{error}</span>}
              </div>
            )}
            onChange={(e,v) => {
              if (!v)
                return;

              const cappedTilt = Math.max(Math.min(this.state.tilt, this.props.antenna_specs[v]["upper_tilt_deg"]), this.props.antenna_specs[v]["lower_tilt_deg"]);

              this.setState({tilt: cappedTilt});
              this.props.change('tilt', cappedTilt);
            }}/>
        </div>
        </form>
        {antenna!=null && antenna !=="Select an antenna..." && antenna.length>0 && <React.Fragment>
            {this.renderAntennaDetails(antenna)}
        <h4>Specify transmit power</h4>  
        <div>
        <div style={{float:"left", width:"16.5%", padding: "15px"}}></div>
        <div style={{float:"left", width:"20%", padding: "0px"}}>Specify EIRP</div>
        <div style={{float:"left", width:"15%", padding: "0px"}}><Switch onChange={this.handleChange} checkedIcon={false} uncheckedIcon={false} checked={this.state.enterPeakPower} /></div>
            <div style={{float:"left", width:"32%", padding: "0px"}}>Specify peak power supplied to load</div>
        </div>
        <br /><br />
        <form onSubmit={handleSubmit}>
        <div>
            <label style={{display: "inline-block", float:"left"}}>EIRP (dBW) 
                <a data-tip data-for='eirp'> 
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="blue" class="bi bi-question-circle" viewBox="0 0 16 16">
                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                        <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286zm1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94z"/>
                    </svg>
                </a>
                <ReactTooltip id='eirp' type='error'>
                    <span>Effective Isotropic Radiated Power or EIRP (in a given direction) is the product of the power supplied to the antenna gain in a given direction relative to an isotropic antenna (absolute or isotropic gain).</span>
            </ReactTooltip>
            </label>
            <div>
                <Field
                    name="eirp_dbw"
                    type="number"
                    component="input"
                    disabled={this.state.enterPeakPower}
                    placeholder=""
                    parse={value => {

                        let res = !value ||isNaN(value) ? 0 : Math.max(Math.min(parseFloat(value), 200), -200)
                        this.props.dispatch(change("newsite",'peak', this.standardizeDecimalPlaces(res-parseFloat(this.props.antenna_specs[antenna]["nominal_gain_dbi"]))))
                        return res
                    }}

                />
            </div>
          </div>
          <div>
            <label style={{display: "inline-block", float:"left"}}>Peak power supplied to the load (dBW) 
                <a data-tip data-for='peak'> 
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="blue" class="bi bi-question-circle" viewBox="0 0 16 16">
                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                        <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286zm1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94z"/>
                    </svg>
                </a>
                <ReactTooltip id='peak' type='error'>
                    <span>The power supplied to the antenna is expressed in the form of peak to peak envelope power (PX or pX). The peak envelope power is defined as the average power supplied to the antenna transmission line by a transmitter during one radio frequency cycle at the crest of the modulation envelope taken under normal operation conditions.</span>
            </ReactTooltip>
            </label>
            <div>
                <Field
                    name="peak"
                    disabled={!this.state.enterPeakPower}
                    type="number"
                    component="input"
                    placeholder=""
                    parse={value => {

                        let res = !value ||isNaN(value) ? 0 : Math.max(Math.min(parseFloat(value), 200), -200)
                        this.props.dispatch(change("newsite",'eirp_dbw', this.standardizeDecimalPlaces(res+parseFloat(this.props.antenna_specs[antenna]["nominal_gain_dbi"]))))
                        return res
                    }}
                />
            </div>
          </div>
          </form>
          <h4>Configure the antenna</h4>
            <form onSubmit={handleSubmit}>
          <Field
            name="elevation_m_AGL"
            type="number"
            component={renderField}
            label="Height Above Ground Level (m)"
            placeholder=""
            parse={value => !value ||isNaN(value) ? 0 : Math.min(Math.max(parseFloat(value), 1), 30)}
          />
          <Field
            name="azimuth"
            type="number"
            component={renderField}
            label="Azimuth (&deg;)"
            placeholder=""
            parse={value => !value ||isNaN(value) ? 0 : this.angleToBearing(parseFloat(value))}
            onChange={(e,v)=>this.setState({"azimuth":v})}
          />
          <Field
            name="tilt"
            type="number"
            component={renderField}
            label="Electrical Downtilt (&deg;)"
            placeholder=""
            parse={value => {
                return Math.max(Math.min(value, this.props.antenna_specs[antenna]["upper_tilt_deg"]), this.props.antenna_specs[antenna]["lower_tilt_deg"])
            }}
            onChange={(e,v)=>this.setState({"tilt":v})}
          />
          </form>
        </React.Fragment>}

        {<div className="button-row">
          <form onSubmit={handleSubmit}>
            <button type="button" className="btn btn-light" onClick={this.props.onCancel}>
                Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={!antenna}>Add</button>
          </form>
        </div>}
      </React.Fragment>
    );
  };

}

TddFormPage = reduxForm({
  form: 'newsite', // a unique identifier for this form
  destroyOnUnmount: true, // <------ preserve form data
  forceUnregisterOnUnmount: true, // <------ unregister fields on unmount
  validate
})(TddFormPage)

const selector = formValueSelector('newsite')

TddFormPage = connect(
  state => {
    let form_state = state
    const antenna = selector(state, 'antenna')

    return {
      form_state,
      antenna
    }
  }
)(TddFormPage)

export default TddFormPage
